// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import {
  Box,
  Grid, Stack, Typography, Pagination
} from "@mui/material";
//TODO : replace the temporary default badge link with the actual badge link
import { certificationBadgeLink, certificationsPage } from "../../config/constant";
import { CertificationsPayload, DocumentTypes } from "../../utils/types";
import SharableLink from "../certificate/SharableLink";
import { useState } from "react";
import DocumentView from "../certificate/DocumentView";

const ITEMS_PER_PAGE = certificationsPage.itemsPerPage
const CertificationsList = (props: {
  certificationData: CertificationsPayload[];
  isOnlyExpiredCertificates: boolean;
}) => {
  const { certificationData, isOnlyExpiredCertificates } = props;
  const [page, setPage] = useState(1);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    
    const year = date.getFullYear();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const pageCount = Math.ceil(certificationData.length / ITEMS_PER_PAGE);
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const displayedCertifications = certificationData.slice(startIndex, endIndex);

  return (
    <Box sx={{ flexGrow: 1, p: 2, mt: 2 }}>
      {certificationData.length !== 0 ? (
        <>
          <Grid container spacing={2}>
            {displayedCertifications.map((certification, index) => (
          <Grid
            key={index}
            item
            {...{ xs: 12, sm: 6, md: 4, lg: 3 }}
            minHeight={160}
            sx={{
              textAlign: "center",
              ...(isOnlyExpiredCertificates && {
                opacity: 0.8,
                filter: "grayscale(100%)",
              }),
            }}
          >
            <Typography
              variant="body2"
              component="h2"
              sx={{ fontWeight: "bold", minHeight: 60 }}
            >
              {certification.displayName || certification.name}
            </Typography>
            <Grid>
              {certification.badgeLink ? (
                <img
                  width={150}
                  src={certification.badgeLink}
                  alt={certification.badgeLink}
                />
              ) : (
                <img
                  width={150}
                  src={
                    certificationBadgeLink[
                      certification.name as keyof typeof certificationBadgeLink
                    ]
                  }
                  alt={`${certification.name} certification badge`}
                />
              )}
            </Grid>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ marginTop: 2 }}
              justifyContent="center"
              >
              {!isOnlyExpiredCertificates ? (
              <>
              <DocumentView document={certification} documentType={DocumentTypes.CERTIFICATE} />
                <SharableLink certificationId={
                  certification.contact.certificationId + "/" + certification.id
                } 
                certification={certification}
                />
              </>
              ) : (            
                <Typography variant="body2" component="h3">
                  {certification.certificationExpirationDate && 
                    (
                      `Expired on ${formatDate(certification.certificationExpirationDate.toString())}`
                    )
                  }
                </Typography>               
              )} 
            </Stack>
            </Grid>
          ))}
        </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={handleChangePage}
              color="primary"
            />
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <img
            src="/no-data.svg"
            alt="No Data"
            height={200}
            style={{ marginTop: 50 }}
          />
          <Typography variant="h6" sx={{ color: "gray", mt: 5 }}>
            No data to show
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CertificationsList;
